// Profile.js - User Profile Page inside Settings
import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config/config";
import { toast } from 'react-toastify';
import LoggingService from "../../services/LoggingService";
import "../../styles/Profile.css";

const Profile = () => {
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    newPassword: ""
  });
  const [showRequirements, setShowRequirements] = useState(false);

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*?.]).{4,}$/;
    return passwordRegex.test(password);
  };

  useEffect(() => {  
    const storedUser = {
        name: localStorage.getItem("userName") || "Unknown User",
        email: localStorage.getItem("userEmail") || "No Email"
    };

    LoggingService.logSystem("Profile Load", `Loading profile for user: ${storedUser.email}`);
    setUser((prev) => ({ ...prev, ...storedUser }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
    
    if (name === 'newPassword') {
      LoggingService.logAction("Password Input", "User started typing new password");
    }
  };

  const handleSave = async () => {
    if (user.newPassword.length > 0) {
      if (!validatePassword(user.newPassword)) {
        LoggingService.logError("Password Validation", "Password validation failed - requirements not met");
        toast.error('Password must contain at least 4 characters, including a number and a special character');
        return;
      }

      try {
        const token = localStorage.getItem("userToken");
        const userId = localStorage.getItem("userId");

        LoggingService.logAction("Password Update", "Verifying if new password is different from current");
        
        const verifyResponse = await axios.post(
          `${config.API_BASE_URL}/auth/verify-password`,
          {
            userId: userId,
            password: user.newPassword
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (verifyResponse.data.matches) {
          LoggingService.logError("Password Update", "New password matches current password");
          toast.error("New password must be different from your current password");
          return;
        }

        LoggingService.logAction("Password Update", "Attempting to update password");
        
        const response = await axios.put(
          `${config.API_BASE_URL}/auth/update-password`,
          {
            userId: userId,
            newPassword: user.newPassword
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.status === 200) {
          LoggingService.logAction("Password Update", "Password updated successfully");
          toast.success("Password updated successfully!");
          setUser({ ...user, newPassword: "" });
          setShowRequirements(false);
        }
      } catch (error) {
        LoggingService.logError("Password Update", `Failed to update password: ${error.message}`);
        toast.error(error.response?.data?.message || "Failed to update password. Please try again.");
      }
    } else {
      LoggingService.logAction("Profile Save", "No changes made to profile");
      toast.info("No changes made.");
    }
  };

  return (
    <div className="profile-container">
      <h2>Profile Settings</h2>
      <label>Name:</label>
      <input type="text" value={user.name} disabled />

      <label>Email:</label>
      <input type="email" value={user.email} disabled />

      <label>New Password:</label>
      <input 
        type="password" 
        name="newPassword" 
        value={user.newPassword} 
        onChange={handleChange}
        onFocus={() => setShowRequirements(true)}
      />
      <div className={`password-requirements ${showRequirements ? 'visible' : ''}`}>
        Password must contain:
        <ul>
          <li>At least 4 characters</li>
          <li>At least one number (0-9)</li>
          <li>At least one special character (!@#$%^&*?.)</li>
        </ul>
      </div>

      <button onClick={handleSave}>Save Changes</button>
    </div>
  );
};

export default Profile;
